import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export const AffiliateDashboardPage = (props) => {
  const {
    loggedAffiliate,
    isAffiliateLoggedIn,
    orders,
    orderCount,
    getAffiliateProfile,
    getAffiliateOrders,
    getAffiliateOrdersCount,
    affiliateLogout,
    withdrawals = [],
    getWithdrawals,
    createWithdrawal,
  } = props;

  const navigate = useNavigate();

  const minLimit = (loggedAffiliate?.withdrawalLimit !== null && parseFloat(loggedAffiliate?.withdrawalLimit) > 0)
    ? parseFloat(loggedAffiliate.withdrawalLimit)
    : 500;

  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [formError, setFormError] = useState("");
  const [monthFilter, setMonthFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;

  const [rewards, setRewards] = useState([]);
  const [loadingRewards, setLoadingRewards] = useState(false);

  const fetchAvailableRewards = async () => {
    const token = sessionStorage.getItem('affiliateToken');
    if (!token) return;
    setLoadingRewards(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:2000/api'}/affiliate-reward/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRewards(res.data.data || []);
    } catch (err) {
      console.error("Error fetching available rewards:", err);
    } finally {
      setLoadingRewards(false);
    }
  };

  const handleClaimReward = async (rewardId, claimedType) => {
    if (!window.confirm("Are you sure you want to claim this reward?")) return;
    const token = sessionStorage.getItem('affiliateToken');
    if (!token) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:2000/api'}/affiliate-reward/${rewardId}/claim`, { claimedType }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Reward claimed successfully! Our team will contact you shortly.");
      fetchAvailableRewards();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to claim reward.");
    }
  };

  const handleMonthFilterChange = (monthVal) => {
    setMonthFilter(monthVal);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!isAffiliateLoggedIn) {
      navigate("/affiliate-login");
    } else {
      getAffiliateProfile();
      getAffiliateOrdersCount(monthFilter);
      getWithdrawals();
      fetchAvailableRewards();
    }
  }, [isAffiliateLoggedIn, monthFilter]);

  useEffect(() => {
    if (isAffiliateLoggedIn) {
      const offset = (currentPage - 1) * ordersPerPage;
      getAffiliateOrders(ordersPerPage, offset, monthFilter);
    }
  }, [currentPage, isAffiliateLoggedIn, monthFilter]);

  const totalOrders = (orderCount?.completed || 0) + (orderCount?.pending || 0);
  const totalPages = Math.max(Math.ceil(totalOrders / ordersPerPage), 1);

  const getPageNumbers = () => {
    const pages = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPageNumbersToShow - 1);
    
    if (endPage - startPage < maxPageNumbersToShow - 1) {
      startPage = Math.max(1, endPage - maxPageNumbersToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleLogout = () => {
    affiliateLogout();
    navigate("/affiliate-login");
  };

  const handleWithdrawSubmit = (e) => {
    e.preventDefault();
    setFormError("");

    const amountNum = parseFloat(withdrawAmount);

    if (isNaN(amountNum) || amountNum < minLimit) {
      setFormError(`Minimum withdrawal amount is ₹${minLimit}`);
      return;
    }

    if (amountNum > (loggedAffiliate?.wallet || 0)) {
      setFormError("Requested amount exceeds your wallet balance");
      return;
    }

    if (!bankName.trim() || !accountHolder.trim() || !accountNumber.trim() || !ifscCode.trim()) {
      setFormError("All bank details are required");
      return;
    }

    createWithdrawal({
      amount: amountNum,
      bankName: bankName.trim(),
      accountHolder: accountHolder.trim(),
      accountNumber: accountNumber.trim(),
      ifscCode: ifscCode.trim()
    }, () => {
      setShowWithdrawModal(false);
      setWithdrawAmount("");
      setBankName("");
      setAccountHolder("");
      setAccountNumber("");
      setIfscCode("");
    });
  };

  const pendingWithdrawalTotal = withdrawals
    .filter(w => w.status === 1)
    .reduce((sum, w) => sum + parseFloat(w.amount), 0);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Coupon code copied to clipboard!");
  };

  const copyLinkToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Referral link copied to clipboard!");
  };

  const couponCode = loggedAffiliate?.Coupons?.[0]?.code || "No coupon code assigned yet";

  return (
    <>
      <style>{`
        .dashboard-container {
          background-color: #f8f9fa;
          min-height: 80vh;
          padding: 40px 0;
        }
        .stat-card {
          border: none;
          border-radius: 16px;
          color: white;
          padding: 24px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }
        .bg-gradient-wallet {
          background: linear-gradient(135deg, #11998e, #38ef7d);
        }
        .bg-gradient-reward {
          background: linear-gradient(135deg, #f857a6, #ff5858);
        }
        .bg-gradient-completed {
          background: linear-gradient(135deg, #4b6cb7, #182848);
        }
        .bg-gradient-pending {
          background: linear-gradient(135deg, #f2994a, #f2c94c);
        }
        .stat-value {
          font-size: 32px;
          font-weight: 700;
          margin-top: 10px;
        }
        .stat-label {
          font-size: 14px;
          opacity: 0.9;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .coupon-box {
          background: #ffffff;
          border: 2px dashed #4b6cb7;
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          margin-bottom: 30px;
          box-shadow: 0 4px 15px rgba(75, 108, 183, 0.05);
        }
        .coupon-code {
          font-size: 24px;
          font-weight: 800;
          color: #4b6cb7;
          letter-spacing: 2px;
          cursor: pointer;
          display: inline-block;
          padding: 5px 15px;
          background: #f0f4ff;
          border-radius: 6px;
          margin-top: 8px;
          transition: all 0.2s;
        }
        .coupon-code:hover {
          background: #e2ebff;
        }
        .dashboard-section {
          background: white;
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
          margin-bottom: 30px;
        }
        .section-title {
          font-size: 18px;
          font-weight: 700;
          color: #333;
          margin-bottom: 20px;
          border-left: 4px solid #4b6cb7;
          padding-left: 12px;
        }
        .info-row {
          display: flex;
          justify-content: space-between;
          padding: 12px 0;
          border-bottom: 1px solid #f1f3f5;
        }
        .info-row:last-child {
          border-bottom: none;
        }
        .info-label {
          color: #6c757d;
          font-weight: 500;
        }
        .info-val {
          color: #333;
          font-weight: 600;
        }
        .custom-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1050;
        }
        .custom-modal-content {
          background: white;
          border-radius: 16px;
          padding: 30px;
          width: 90%;
          max-width: 500px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          animation: slideUp 0.3s ease-out;
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .custom-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #f1f3f5;
          padding-bottom: 15px;
          margin-bottom: 20px;
        }
        .custom-modal-title {
          font-size: 20px;
          font-weight: 700;
          color: #333;
        }
        .custom-modal-close {
          border: none;
          background: none;
          font-size: 24px;
          cursor: pointer;
          color: #6c757d;
        }
        .custom-modal-close:hover {
          color: #333;
        }
        .text-pending-warning {
          color: #fffbeb;
          font-size: 12px;
          margin-top: 4px;
        }
        .custom-modal-content label {
          display: block !important;
          position: relative !important;
          transform: none !important;
          margin-bottom: 6px !important;
          color: #495057 !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          text-align: left !important;
          pointer-events: auto !important;
          opacity: 1 !important;
        }
        .custom-modal-content input {
          display: block !important;
          position: relative !important;
          background-color: #ffffff !important;
          color: #333333 !important;
          border: 1px solid #ced4da !important;
          border-radius: 6px !important;
          padding: 8px 12px !important;
          width: 100% !important;
          font-size: 14px !important;
          box-shadow: none !important;
          height: 40px !important;
          margin-top: 0 !important;
        }
      `}</style>

      <div className="full-row bg-light overlay-dark py-4"
        style={{backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/banner.png)`, backgroundPosition: "center center", backgroundSize: "cover"}}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8 text-white text-center text-md-start">
              <h3 className="mb-2 text-white font-weight-bold">Influencer Portal</h3>
              <p className="mb-0 text-white opacity-75">Welcome back, {loggedAffiliate?.firstName}!</p>
            </div>
            <div className="col-md-4 text-center text-md-end mt-3 mt-md-0">
              <button onClick={handleLogout} className="btn btn-outline-light rounded-pill px-4 py-2">
                <i className="fa-solid fa-right-from-bracket me-2"></i>Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-container">
        <div className="container">
          {/* Stats Grid */}
          <div className="row g-4 mb-4">
            <div className="col-lg-3 col-sm-6">
              <div className="stat-card bg-gradient-wallet d-flex flex-column justify-content-between h-100">
                <div>
                  <div className="stat-label">Wallet Balance</div>
                  <div className="stat-value">₹{loggedAffiliate?.wallet || 0}</div>
                  {pendingWithdrawalTotal > 0 && (
                    <div className="text-pending-warning fw-bold">
                      <i className="fa-solid fa-clock me-1"></i>Pending: ₹{pendingWithdrawalTotal}
                    </div>
                  )}
                  <div className="mt-2 text-white-50 small">Credited from complete sales</div>
                </div>
                <div>
                  <button 
                    onClick={() => setShowWithdrawModal(true)} 
                    className="btn btn-light btn-sm w-100 rounded-pill mt-3 font-weight-bold"
                    style={{ color: '#11998e' }}
                  >
                    <i className="fa-solid fa-money-bill-transfer me-1"></i>Withdraw Funds
                  </button>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-sm-6">
              <div className="stat-card bg-gradient-reward">
                <div className="stat-label">Total Earnings</div>
                <div className="stat-value">₹{loggedAffiliate?.reward || 0}</div>
                <div className="mt-2 text-white-50 small">Lifetime rewards earned</div>
              </div>
            </div>
            <div className="col-lg-3 col-sm-6">
              <div className="stat-card bg-gradient-completed">
                <div className="stat-label">Completed Sales</div>
                <div className="stat-value">{orderCount?.completed || 0}</div>
                <div className="mt-2 text-white-50 small">Approved & delivered orders</div>
              </div>
            </div>
            <div className="col-lg-3 col-sm-6">
              <div className="stat-card bg-gradient-pending">
                <div className="stat-label">Pending Sales</div>
                <div className="stat-value">{orderCount?.pending || 0}</div>
                <div className="mt-2 text-white-50 small">Orders awaiting completion</div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Left: Promo/Coupon Box and Profile */}
            <div className="col-lg-4">
              <div className="coupon-box">
                <div className="text-secondary fw-bold small text-uppercase">Your Promo Code</div>
                <div className="coupon-code" onClick={() => copyToClipboard(couponCode)}>
                  {couponCode} <i className="fa-regular fa-copy ms-1 small"></i>
                </div>
                <div className="text-secondary mt-2 small">
                  Share this code with your followers to give them a discount and earn 50% of the discount amount in your wallet!
                </div>
              </div>

              <div className="coupon-box mt-3" style={{ background: '#f0f4ff', border: '1px solid #d0e0ff' }}>
                <div className="text-secondary fw-bold small text-uppercase" style={{ color: '#1a73e8' }}>Your Referral Link</div>
                <div className="coupon-code" style={{ wordBreak: 'break-all', fontSize: '13px', background: '#e8f0fe', color: '#1a73e8' }} onClick={() => copyLinkToClipboard(`${window.location.origin}/?ref=${loggedAffiliate?.Coupons?.[0]?.code ? loggedAffiliate.Coupons[0].code : loggedAffiliate?.id || ""}`)}>
                  {`${window.location.origin}/?ref=${loggedAffiliate?.Coupons?.[0]?.code ? loggedAffiliate.Coupons[0].code : loggedAffiliate?.id || ""}`} <i className="fa-regular fa-copy ms-1 small"></i>
                </div>
                <div className="text-secondary mt-2 small">
                  Share this link with your followers. Any shopping done through this link will automatically apply your discount code and credit you!
                </div>
              </div>

              <div className="dashboard-section">
                <div className="section-title">My Profile Details</div>
                <div className="info-row">
                  <span className="info-label">Full Name</span>
                  <span className="info-val">{loggedAffiliate?.firstName} {loggedAffiliate?.lastName}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email Address</span>
                  <span className="info-val">{loggedAffiliate?.email}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Phone Number</span>
                  <span className="info-val">{loggedAffiliate?.phone}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Gender / Age</span>
                  <span className="info-val">{loggedAffiliate?.gender} / {loggedAffiliate?.age}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Address</span>
                  <span className="info-val">{loggedAffiliate?.city}, {loggedAffiliate?.state}</span>
                </div>
              </div>

              <div className="dashboard-section mt-3">
                <div className="section-title">🎁 Available Incentives & Rewards</div>
                {loadingRewards ? (
                  <div className="text-center py-3">
                    <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
                    <span className="ms-2 text-secondary small">Loading incentives...</span>
                  </div>
                ) : rewards.length === 0 ? (
                  <div className="text-secondary small py-2">
                    No active rewards or trips available for claims at this time. Check back later!
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3 mt-2">
                    {rewards.map((reward) => (
                      <div 
                        key={reward.id} 
                        className="p-3 border rounded bg-white shadow-sm"
                        style={{ borderLeft: reward.rewardType === 'trip' ? '4px solid #28a745' : (reward.rewardType === 'cash' ? '4px solid #fd7e14' : '4px solid #17a2b8') }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="fw-bold text-dark small">{reward.title}</span>
                          <span className={`badge ${reward.rewardType === 'trip' ? 'bg-success' : (reward.rewardType === 'cash' ? 'bg-warning' : 'bg-info')} text-white small`} style={{ fontSize: '10px' }}>
                            {reward.rewardType === 'trip' ? '🌴 Trip' : (reward.rewardType === 'cash' ? '💰 Cash' : '🎁 Trip or Cash')}
                          </span>
                        </div>
                        <p className="text-secondary mb-2" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                          {reward.description || 'No description provided.'}
                        </p>
                        {reward.rewardType === 'both' ? (
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-success btn-sm w-50 py-1"
                              style={{ fontSize: '12px', fontWeight: 'bold' }}
                              onClick={() => handleClaimReward(reward.id, 'trip')}
                            >
                              Claim Trip
                            </button>
                            <button
                              className="btn btn-warning text-white btn-sm w-50 py-1"
                              style={{ fontSize: '12px', fontWeight: 'bold' }}
                              onClick={() => handleClaimReward(reward.id, 'cash')}
                            >
                              Claim Cash
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-primary btn-sm w-100 py-1"
                            style={{ fontSize: '12px', fontWeight: 'bold' }}
                            onClick={() => handleClaimReward(reward.id, reward.rewardType)}
                          >
                            Claim Reward
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Recent Sales list */}
            <div className="col-lg-8">
              <div className="dashboard-section">
                <div className="section-title d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>Recent Coupon Uses & Sales</span>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-secondary small fw-bold">Filter by Month:</span>
                    <input 
                      type="month" 
                      value={monthFilter}
                      onChange={(e) => handleMonthFilterChange(e.target.value)}
                      className="form-control form-control-sm"
                      style={{ width: '150px' }}
                    />
                    {monthFilter && (
                      <button 
                        className="btn btn-link btn-sm text-decoration-none py-0 px-1"
                        onClick={() => handleMonthFilterChange('')}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                {orders.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fa-solid fa-bag-shopping fs-1 text-muted opacity-50 mb-3"></i>
                    <p className="text-secondary">No orders have been placed using your coupon code yet.</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table align-middle table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Order ID</th>
                            <th>Customer</th>
                            <th>Products Purchased</th>
                            <th>Coupon Code</th>
                            <th>Sale Source</th>
                            <th>Date</th>
                            <th>Order Amount</th>
                            <th>Discount</th>
                            <th>Your Reward</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map((o) => {
                            const getOrderStatusInfo = (status) => {
                              switch (status) {
                                case 1:
                                  return { label: "Pending", badge: "bg-warning text-dark" };
                                case 2:
                                  return { label: "Accepted", badge: "bg-info text-dark" };
                                case 3:
                                  return { label: "Dispatched", badge: "bg-info text-dark" };
                                case 4:
                                  return { label: "In Transit", badge: "bg-info text-dark" };
                                case 5:
                                  return { label: "Delivered", badge: "bg-success text-white" };
                                case 6:
                                  return { label: "Cancelled", badge: "bg-danger text-white" };
                                case 7:
                                  return { label: "Refunded", badge: "bg-danger text-white" };
                                case 8:
                                  return { label: "Return Requested", badge: "bg-secondary text-white" };
                                case 9:
                                  return { label: "Return In Transit", badge: "bg-warning text-dark" };
                                case 10:
                                  return { label: "Returned", badge: "bg-danger text-white" };
                                case 11:
                                  return { label: "Return Rejected", badge: "bg-success text-white" };
                                default:
                                  return { label: "Processing", badge: "bg-info text-dark" };
                              }
                            };
                            const statusInfo = getOrderStatusInfo(o.status);
                            const orderStatus = statusInfo.label;
                            const statusBadge = statusInfo.badge;
                            
                            let purchasedProducts = [];
                            try {
                              purchasedProducts = JSON.parse(o.cart || "[]");
                            } catch (e) {
                              console.error("Cart parsing error:", e);
                            }

                            const isReverted = [6, 7, 10].includes(o.status);

                            return (
                              <tr key={o.id}>
                                <td><strong>#{o.id}</strong></td>
                                <td>
                                  <div className="fw-bold">{o.billingName}</div>
                                  <div className="small text-muted">{o.billingEmail}</div>
                                  <div className="small text-muted">{o.billingPhone}</div>
                                </td>
                                <td>
                                  {purchasedProducts.map((item, idx) => (
                                    <div key={idx} className="small text-dark mb-1">
                                      • {item?.Product?.name} <strong className="text-secondary">x{item?.quantity}</strong>
                                    </div>
                                  ))}
                                </td>
                                <td>
                                  <span className="badge bg-secondary font-weight-bold" style={{ letterSpacing: '0.5px' }}>
                                    {o.couponCode || "N/A"}
                                  </span>
                                </td>
                                <td>
                                  {!o.couponCode || o.couponCode.toUpperCase() !== couponCode.toUpperCase() ? (
                                    <span className="badge bg-info text-dark">Referral Link</span>
                                  ) : (
                                    <span className="badge bg-primary">Coupon Code</span>
                                  )}
                                </td>
                                <td>{new Date(o.createdAt).toLocaleDateString()}</td>
                                <td><strong>₹{o.finalAmount}</strong></td>
                                <td className="text-danger">₹{o.discount}</td>
                                <td className={isReverted ? "text-danger fw-bold" : "text-success fw-bold"}>
                                  {isReverted ? "-" : ""}₹{o.affiliateReward || 0}
                                </td>
                                <td>
                                  <span className={`badge ${statusBadge}`}>{orderStatus}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                        <div className="small text-muted">
                          Showing <strong>{((currentPage - 1) * ordersPerPage) + 1}</strong> to <strong>{Math.min(currentPage * ordersPerPage, totalOrders)}</strong> of <strong>{totalOrders}</strong> orders
                        </div>
                        <nav aria-label="Page navigation">
                          <ul className="pagination mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                              >
                                Previous
                              </button>
                            </li>
                            {getPageNumbers().map(pageNum => (
                              <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                <button 
                                  className="page-link" 
                                  onClick={() => setCurrentPage(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Withdrawal History Section */}
              <div className="dashboard-section mt-4">
                <div className="section-title">Withdrawal Requests & Payouts</div>
                {withdrawals.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fa-solid fa-money-bill-transfer fs-1 text-muted opacity-50 mb-3"></i>
                    <p className="text-secondary">No withdrawal requests submitted yet.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Bank Name</th>
                          <th>Account Holder & No.</th>
                          <th>IFSC</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {withdrawals.map((w) => {
                          const statusText = w.status === 1 ? "Pending" : w.status === 2 ? "Approved" : "Rejected";
                          const statusBadge = w.status === 2 ? "bg-success" : w.status === 1 ? "bg-warning text-dark" : "bg-danger";
                          return (
                            <tr key={w.id}>
                              <td>{new Date(w.createdAt).toLocaleDateString()}</td>
                              <td><strong>₹{w.amount}</strong></td>
                              <td>{w.bankName}</td>
                              <td>
                                <div className="fw-bold">{w.accountHolder}</div>
                                <div className="small text-muted">{w.accountNumber}</div>
                              </td>
                              <td>{w.ifscCode}</td>
                              <td>
                                <span className={`badge ${statusBadge}`}>{statusText}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="custom-modal-overlay" onClick={() => setShowWithdrawModal(false)}>
          <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="custom-modal-header">
              <span className="custom-modal-title">Withdraw Funds</span>
              <button className="custom-modal-close" onClick={() => setShowWithdrawModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleWithdrawSubmit}>
              <div className="mb-3 text-secondary small">
                Transfer earnings to your bank account. Minimum withdrawal amount is <strong>₹{minLimit}</strong>.
                <div className="mt-1">Available wallet balance: <strong>₹{loggedAffiliate?.wallet || 0}</strong></div>
                {pendingWithdrawalTotal > 0 && <div>Currently pending withdrawal: <strong>₹{pendingWithdrawalTotal}</strong></div>}
              </div>

              {formError && (
                <div className="alert alert-danger py-2 px-3 small mb-3">
                  <i className="fa-solid fa-triangle-exclamation me-2"></i>{formError}
                </div>
              )}

              <div className="form-group mb-3">
                <label className="form-label small fw-bold">Amount to Withdraw (₹)</label>
                <input 
                  type="number" 
                  className="form-control"
                  placeholder={`e.g. ${minLimit}`} 
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  min={minLimit}
                  max={loggedAffiliate?.wallet || 0}
                  required
                />
              </div>

              <div className="form-group mb-3">
                <label className="form-label small fw-bold">Bank Name</label>
                <input 
                  type="text" 
                  className="form-control"
                  placeholder="State Bank of India" 
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group mb-3">
                <label className="form-label small fw-bold">Account Holder Name</label>
                <input 
                  type="text" 
                  className="form-control"
                  placeholder="Name as in bank records" 
                  value={accountHolder}
                  onChange={(e) => setAccountHolder(e.target.value)}
                  required
                />
              </div>

              <div className="form-group mb-3">
                <label className="form-label small fw-bold">Account Number</label>
                <input 
                  type="text" 
                  className="form-control"
                  placeholder="Account Number" 
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  required
                />
              </div>

              <div className="form-group mb-3">
                <label className="form-label small fw-bold">IFSC Code</label>
                <input 
                  type="text" 
                  className="form-control"
                  placeholder="SBIN0001234" 
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value)}
                  required
                />
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button 
                  type="button" 
                  className="btn btn-outline-secondary btn-sm px-3 rounded-pill" 
                  onClick={() => setShowWithdrawModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-success btn-sm px-4 rounded-pill"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
